import { createContext, useState, useCallback, useContext, useEffect, useRef } from 'react';
import { Invoice } from '../types';
import _ from 'lodash';
import AuthContext from './AuthContext';
import { useInvoices } from '../hooks/useInvoices';
import { getAmountToPay } from '../helpers/amountToPay';

type InvoiceToPay = Invoice & {
  paymentIsMinimum: boolean;
  partialAmount?: number;
  index: number;
};

type InvoicesToPayContextType = {
  invoicesToPay: InvoiceToPay[];
  addInvoiceToPay: (invoice: Invoice, paymentIsMinimum: boolean, index: number) => void;
  maxIndexSelected: number;
  removeInvoiceToPay: (invoice: Invoice, enforceOrderedPayments: boolean) => void;
  setPartialAmount: (invoice: Invoice, index: number, partialAmount?: number) => void;
  clearInvoicesToPay: () => void;
  totalAmountToPay: number;
  invoiceSelectionLocked: boolean; // TODO: rename to isProcessingPayment / isPayingInvoice
  setInvoiceSelectionLocked: (invoiceSelectionLocked: boolean) => void;
};

const InvoicesToPayContext = createContext<InvoicesToPayContextType>({
  invoicesToPay: [],
  addInvoiceToPay: () => {},
  maxIndexSelected: 0,
  removeInvoiceToPay: () => {},
  setPartialAmount: () => {},
  clearInvoicesToPay: () => {},
  totalAmountToPay: 0,
  invoiceSelectionLocked: false,
  setInvoiceSelectionLocked: () => {},
});

const InvoicesToPayProvider = ({ children }: React.PropsWithChildren): JSX.Element => {
  const [invoicesToPay, setInvoicesToPay] = useState<InvoiceToPay[]>([]);
  const { user } = useContext(AuthContext);
  const { readyInvoices, isLoading } = useInvoices();
  const hasAutoSelectedRef = useRef(false);
  const [invoiceSelectionLocked, setInvoiceSelectionLocked] = useState(false);

  const addInvoiceToPay = useCallback((invoice: Invoice, paymentIsMinimum: boolean, index: number) => {
    setInvoicesToPay((prevInvoices) => [
      ...prevInvoices.filter((ele) => ele.id !== invoice.id),
      { ...invoice, paymentIsMinimum, index },
    ]);
  }, []);

  const setPartialAmount = useCallback((invoice: Invoice, index: number, partialAmount?: number) => {
    setInvoicesToPay((prevInvoices) => [
      ...prevInvoices.filter((ele) => ele.id !== invoice.id),
      { ...invoice, paymentIsMinimum: false, index, partialAmount },
    ]);
  }, []);

  const removeInvoiceToPay = useCallback(
    (invoice: Invoice, enforceOrderedPayments: boolean) => {
      const index = invoicesToPay.find((i) => i.id === invoice.id)?.index ?? -1;
      setInvoicesToPay((prevInvoices) =>
        prevInvoices.filter((i) => (!enforceOrderedPayments || i.index < index) && i.id !== invoice.id)
      );
    },
    [invoicesToPay]
  );

  const clearInvoicesToPay = useCallback(() => {
    setInvoicesToPay([]);
    setInvoiceSelectionLocked(false);
  }, []);

  useEffect(() => {
    // Clear invoices when user logs out
    if (!user) {
      clearInvoicesToPay();
      hasAutoSelectedRef.current = false;
    }
  }, [user, clearInvoicesToPay]);

  useEffect(() => {
    // Clear invoices when all selected invoices have been paid (no longer in readyInvoices)
    // This handles the case where user has paid all invoices

    if (isLoading || readyInvoices.length === 0) return;
    if (invoicesToPay.length === 0) return;

    // Verify that all selected invoices are no longer in readyInvoices (they were paid)
    const readyInvoiceIds = new Set(readyInvoices.map((inv) => inv.id));
    const allInvoicesToPayArePaid = invoicesToPay.every((invoice) => !readyInvoiceIds.has(invoice.id));
    if (allInvoicesToPayArePaid) clearInvoicesToPay();
  }, [isLoading, readyInvoices, invoicesToPay, clearInvoicesToPay]);

  useEffect(() => {
    // Auto-select first unpaid invoice when:
    // 1. User is logged in
    // 2. There are ready invoices
    // 3. No invoices are currently selected
    // 4. We haven't already auto-selected (prevents re-selection after clearing)
    if (!user || readyInvoices.length === 0 || invoicesToPay.length > 0 || hasAutoSelectedRef.current) return;

    const unpaidInvoice = readyInvoices.find((ele) => !ele.paymentDate);
    if (unpaidInvoice) {
      addInvoiceToPay(unpaidInvoice, false, 0);
      hasAutoSelectedRef.current = true;
    }
  }, [user, readyInvoices, invoicesToPay.length, addInvoiceToPay, isLoading]);

  return (
    <InvoicesToPayContext.Provider
      value={{
        invoicesToPay,
        addInvoiceToPay,
        removeInvoiceToPay,
        setPartialAmount,
        clearInvoicesToPay,
        totalAmountToPay: _.sumBy(invoicesToPay, getAmountToPay),
        maxIndexSelected: _.maxBy(invoicesToPay, (invoice) => invoice.index)?.index ?? -1,
        invoiceSelectionLocked,
        setInvoiceSelectionLocked,
      }}
    >
      {children}
    </InvoicesToPayContext.Provider>
  );
};
export default InvoicesToPayContext;

export { InvoicesToPayProvider };
